<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { api, type GgeClSnapshotsResponse } from '../api/client'
import { sparkSegments } from './sparkline'

// GGE promotion Phase 5 (2026-08-19): the JH-vs-NAS100 sparkline pattern,
// scoped to the GGE fund's decision feed. Two mini sparks side-by-side:
//   - GGE: daily HL-factsheet NAV (pence)  →  value = 1d %
//   - CL:  WTI front-month (CL.n.0)        →  value = today vs prior settle %
// Clicking either navigates to the full ChartView route /charts/ggevscl.

const data = ref<GgeClSnapshotsResponse | null>(null)
const loading = ref(true)

onMounted(async () => {
  try {
    data.value = await api.ggeClSnapshots('5d')
  } catch { /* leave null */ }
  loading.value = false
})

function goChart(path: string) {
  if (window.location.pathname === path) return
  window.history.pushState({}, '', path)
  window.dispatchEvent(new PopStateEvent('popstate'))
}

// Bloomberg-style per-segment colouring — sparkSegments from ./sparkline (2026-11-02).

// CL spark: chronological CL close series from the downsampled bars.
const clSpark = computed(() => {
  const pts = (data.value?.clBars ?? []).map(b => b.close)
  return { segments: sparkSegments(pts, 80, 18, /* upIsGood */ true), values: pts }
})

// GGE spark: daily NAV pence series (once-daily strike, so the spark shows
// the tail of the 5d window — usually 3-5 points depending on trading days).
const ggeSpark = computed(() => {
  const pts = (data.value?.ggeNav ?? []).map(r => r.pence)
  return { segments: sparkSegments(pts, 80, 18, /* upIsGood */ true), values: pts }
})

// Value columns: % change over the displayed window (first vs last point).
// Matches BGA/JH SparklineBar "fund/nas return" semantics — the header pill
// answers "how much has this moved over the window we're showing".
function pctChange(pts: number[]): number | null {
  if (pts.length < 2 || pts[0] === 0) return null
  return (pts[pts.length - 1] / pts[0]) - 1
}
const clPct = computed(() => pctChange(clSpark.value.values))
const ggePct = computed(() => pctChange(ggeSpark.value.values))

function fmtPct(v: number | null): string {
  if (v == null) return '—'
  return (v >= 0 ? '+' : '') + (v * 100).toFixed(2) + '%'
}
function returnClass(v: number | null): string {
  if (v == null) return ''
  return v >= 0 ? 'positive' : 'negative'
}
</script>

<template>
  <div v-if="!loading" class="sparkline-bar" data-testid="gge-cl-mini">
    <!-- GGE NAV — up=green (long the fund) -->
    <div class="spark-item" data-testid="spark-gge" @click="goChart('/charts/ggevscl')">
      <span class="spark-label gge">GGE</span>
      <svg v-if="ggeSpark.segments.length" viewBox="0 0 80 18" preserveAspectRatio="none" class="spark-svg">
        <line v-for="(s, i) in ggeSpark.segments" :key="`g${i}`"
              :x1="s.x1" :y1="s.y1" :x2="s.x2" :y2="s.y2"
              :stroke="s.stroke" stroke-width="1.5" />
      </svg>
      <svg v-else viewBox="0 0 80 18" preserveAspectRatio="none" class="spark-svg">
        <line x1="0" y1="9" x2="80" y2="9" stroke="#1c2128" stroke-width="1" stroke-dasharray="4,3" />
      </svg>
      <span class="spark-value" :class="returnClass(ggePct)">{{ fmtPct(ggePct) }}</span>
    </div>

    <!-- CL WTI — up=green (the signal fires BUY when CL runs above prior close) -->
    <div class="spark-item" data-testid="spark-cl" @click="goChart('/charts/ggevscl')">
      <span class="spark-label cl">CL</span>
      <svg v-if="clSpark.segments.length" viewBox="0 0 80 18" preserveAspectRatio="none" class="spark-svg">
        <line v-for="(s, i) in clSpark.segments" :key="`c${i}`"
              :x1="s.x1" :y1="s.y1" :x2="s.x2" :y2="s.y2"
              :stroke="s.stroke" stroke-width="1.5" />
      </svg>
      <svg v-else viewBox="0 0 80 18" preserveAspectRatio="none" class="spark-svg">
        <line x1="0" y1="9" x2="80" y2="9" stroke="#1c2128" stroke-width="1" stroke-dasharray="4,3" />
      </svg>
      <span class="spark-value" :class="returnClass(clPct)">{{ fmtPct(clPct) }}</span>
    </div>
  </div>
</template>

<style scoped>
.sparkline-bar {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  align-items: center;
  gap: 16px;
  padding: 6px 12px;
  background: #0d1117;
  border: 1px solid #1c2128;
  border-radius: 6px;
  margin-bottom: 8px;
}
.spark-item { display: flex; align-items: center; gap: 6px; min-width: 0; cursor: pointer; padding: 2px 4px; border-radius: 4px; }
.spark-item:hover { background: #161b22; }
.spark-svg { flex: 1 1 auto; min-width: 0; width: 100%; height: 18px; }
.spark-label { font-size: 10px; font-family: 'JetBrains Mono', monospace; min-width: 24px; flex: 0 0 auto; }
.spark-label.gge { color: #ff9800; }   /* GGE orange — energy */
.spark-label.cl  { color: #26c6da; }   /* CL cyan — matches FX in SparklineBar */
.spark-value { font-size: 10px; font-family: 'JetBrains Mono', monospace; color: #888; min-width: 48px; flex: 0 0 auto; text-align: right; }
.spark-value.positive { color: #4caf50; }
.spark-value.negative { color: #f44336; }

@media (max-width: 768px) {
  .sparkline-bar { gap: 8px; padding: 4px 8px; }
  .spark-label { font-size: 9px; }
  .spark-value { font-size: 9px; }
}
</style>
